import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Chip,
    Card,
    CardContent,
    CardActionArea,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    Alert,
    CircularProgress,
    useMediaQuery,
    useTheme,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    MoreVert as MoreVertIcon,
    Inventory2 as InventoryIcon,
    Print as PrintIcon,
    QrCode2 as QrIcon,
    Close as CloseIcon,
    CheckBox as CheckBoxIcon,
    CheckBoxOutlineBlank as CheckBoxBlankIcon,
    Person as PersonIcon,
    LocalShipping as VozidloIcon,
    Home as StaniceIcon,
    HourglassEmpty as CekaIcon,
    Settings as SettingsIcon,
} from '@mui/icons-material';
import { PageHeader, ViewToggle } from '../../components/admin';
import type { ViewMode } from '../../components/admin';
import type {
    InventarPolozka,
    InventarKategorie,
    CreateInventarPolozkaRequest,
} from '../../api/JsdhNotifikaceService/generated/models/Inventar';
import {
    useAllInventarPolozkyQuery,
    useInventarKategorieQuery,
    useCreateInventarPolozkaMutation,
    useUpdateInventarPolozkaMutation,
    useDeleteInventarPolozkaMutation,
    useVelikostTypyQuery,
    useDownloadTiskPdfMutation,
    useCekajiciNaInvCisloQuery,
    useDoplnInventarniCislaMutation,
} from '../../api/JsdhNotifikaceService/service/InventarMutations';
import { useTypyKontrolQuery } from '../../api/JsdhNotifikaceService/service/KontrolyMutations';
import { getKategorieIcon } from './InventarKategoriePage';
import type { VelikostPole } from '../../api/JsdhNotifikaceService/generated/models/Inventar';

const QR_FORMATY = [
    { kod: 'AVERY_3474', nazev: 'Avery 3474 — 70×37mm (24 ks/A4)' },
    { kod: 'AVERY_3652', nazev: 'Avery 3652 — 70×42mm (21 ks/A4)' },
    { kod: 'AVERY_3651', nazev: 'Avery 3651 — 52.5×30mm (40 ks/A4)' },
    { kod: 'AVERY_L7160', nazev: 'Avery L7160 — 63.5×38mm (21 ks/A4)' },
    { kod: 'AVERY_L7161', nazev: 'Avery L7161 — 63.5×47mm (18 ks/A4)' },
    { kod: 'AVERY_3422', nazev: 'Avery 3422 — 70×35mm (24 ks/A4)' },
    { kod: 'AVERY_3475', nazev: 'Avery 3475 — 70×36mm (24 ks/A4)' },
    { kod: 'AVERY_L7163', nazev: 'Avery L7163 — 99×38mm (14 ks/A4)' },
];

const StavChip: React.FC<{ stav: string }> = ({ stav }) => {
    const map: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
        'AKTIVNI': { label: 'Aktivní', color: 'success' },
        'V_OPRAVE': { label: 'V opravě', color: 'warning' },
        'VYRAZENO': { label: 'Vyřazeno', color: 'error' },
        'ZTRACENO': { label: 'Ztraceno', color: 'error' },
    };
    const { label, color } = map[stav] ?? { label: stav, color: 'default' as const };
    return <Chip label={label} color={color} size="small" />;
};

const StavInventareChip: React.FC<{ stav: string }> = ({ stav }) => {
    if (stav === 'CEKA_NA_INVENTARNI_CISLO') {
        return <Chip icon={<CekaIcon />} label="Čeká na účetní" variant="outlined" size="small" color="warning" />;
    }
    if (stav === 'EVIDOVANO') {
        return <Chip label="Evidováno" size="small" color="success" variant="outlined" />;
    }
    return <Chip label={stav} size="small" variant="outlined" />;
};

const PrirazeniChip: React.FC<{ typ: string; nazev: string }> = ({ typ, nazev }) => {
    if (typ === 'CLEN') return <Chip icon={<PersonIcon />} label={nazev} size="small" variant="outlined" />;
    if (typ === 'VOZIDLO') return <Chip icon={<VozidloIcon />} label={nazev} size="small" variant="outlined" />;
    return <Chip icon={<StaniceIcon />} label="Stanice" size="small" variant="outlined" />;
};

const InventarPage: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [viewMode, setViewMode] = useState<ViewMode>('table');
    const [kategorieFilter, setKategorieFilter] = useState<number | ''>('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [qrDialogOpen, setQrDialogOpen] = useState(false);
    const [qrDialogFormat, setQrDialogFormat] = useState('AVERY_3474');
    const [doplnitDialogOpen, setDoplnitDialogOpen] = useState(false);
    const [doplnitCisla, setDoplnitCisla] = useState<Record<number, string>>({});

    // Form state
    const [formNazev, setFormNazev] = useState('');
    const [formKategorieId, setFormKategorieId] = useState<number | ''>('');
    const [formSerioveCislo, setFormSerioveCislo] = useState('');
    const [formVyrobce, setFormVyrobce] = useState('');
    const [formModel, setFormModel] = useState('');
    const [formVelikost, setFormVelikost] = useState('');
    const [formVelikostTypId, setFormVelikostTypId] = useState<number | ''>('');
    const [formVelikostValues, setFormVelikostValues] = useState<string[]>([]);
    const [formInventarniCislo, setFormInventarniCislo] = useState('');
    const [formPoznamka, setFormPoznamka] = useState('');
    const [formTypKontrolyId, setFormTypKontrolyId] = useState<number | ''>('');
    const [editingId, setEditingId] = useState<number | null>(null);

    // Context menu
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [menuPolozka, setMenuPolozka] = useState<InventarPolozka | null>(null);

    const params = kategorieFilter ? { kategorieId: kategorieFilter as number } : undefined;
    const { data: polozky, isLoading } = useAllInventarPolozkyQuery(params);
    const { data: kategorie } = useInventarKategorieQuery();
    const { data: velikostTypy } = useVelikostTypyQuery();
    const { data: typyKontrol } = useTypyKontrolQuery();
    const typyRevizeOOP = (typyKontrol ?? []).filter(t => t.target === 'INVENTAR' && t.aktivni);
    const kategorieIconMap = new Map(kategorie?.map(k => [k.id, k.icon]) ?? []);

    // Helper: parse JSON pole z vybraného typu velikosti
    const selectedVelikostTyp = velikostTypy?.find((t: any) => t.id === formVelikostTypId);
    const velikostPole: VelikostPole[] = selectedVelikostTyp ? (() => { try { return JSON.parse(selectedVelikostTyp.pole); } catch { return []; } })() : [];
    const createMutation = useCreateInventarPolozkaMutation();
    const updateMutation = useUpdateInventarPolozkaMutation();
    const deleteMutation = useDeleteInventarPolozkaMutation();
    const downloadTiskPdfMutation = useDownloadTiskPdfMutation();
    const { data: cekajiciPolozky, isLoading: cekajiciLoading } = useCekajiciNaInvCisloQuery({ enabled: doplnitDialogOpen });
    const doplnInventarniCislaMutation = useDoplnInventarniCislaMutation();

    const resetForm = () => {
        setFormNazev('');
        setFormKategorieId('');
        setFormSerioveCislo('');
        setFormVyrobce('');
        setFormModel('');
        setFormVelikost('');
        setFormVelikostTypId('');
        setFormVelikostValues([]);
        setFormInventarniCislo('');
        setFormPoznamka('');
        setFormTypKontrolyId('');
        setEditingId(null);
    };

    const openCreate = () => {
        resetForm();
        setDialogOpen(true);
    };

    const openEdit = (p: InventarPolozka) => {
        setEditingId(p.id);
        setFormNazev(p.nazev);
        setFormKategorieId(p.kategorieId);
        setFormSerioveCislo(p.serioveCislo ?? '');
        setFormVyrobce(p.vyrobce ?? '');
        setFormModel(p.model ?? '');
        setFormVelikost(p.velikost ?? '');
        setFormVelikostTypId(p.velikostTypId ?? '');
        setFormVelikostValues(p.velikost ? p.velikost.split(' / ') : []);
        setFormInventarniCislo(p.inventarniCislo ?? '');
        setFormPoznamka(p.poznamka ?? '');
        setFormTypKontrolyId(p.typKontrolyId ?? '');
        setDialogOpen(true);
    };

    const handleSubmit = () => {
        if (!formNazev || !formKategorieId) return;
        if (editingId) {
            updateMutation.mutate({ id: editingId, data: {
                nazev: formNazev,
                kategorieId: formKategorieId as number,
                serioveCislo: formSerioveCislo || undefined,
                vyrobce: formVyrobce || undefined,
                model: formModel || undefined,
                velikost: (formVelikostValues.filter(Boolean).join(' / ') || formVelikost) || undefined,
                velikostTypId: formVelikostTypId || undefined,
                inventarniCislo: formInventarniCislo || undefined,
                poznamka: formPoznamka || undefined,
                // Editace: prázdná volba "Bez revizí" musí explicitně smazat vazbu (BE bere 0 = smazat, null/chybí = beze změny)
                typKontrolyId: formTypKontrolyId || 0,
            }}, { onSuccess: () => { setDialogOpen(false); resetForm(); } });
        } else {
            createMutation.mutate({
                nazev: formNazev,
                kategorieId: formKategorieId as number,
                serioveCislo: formSerioveCislo || undefined,
                vyrobce: formVyrobce || undefined,
                model: formModel || undefined,
                velikost: (formVelikostValues.filter(Boolean).join(' / ') || formVelikost) || undefined,
                velikostTypId: formVelikostTypId || undefined,
                inventarniCislo: formInventarniCislo || undefined,
                poznamka: formPoznamka || undefined,
                typKontrolyId: formTypKontrolyId || undefined,
            }, { onSuccess: () => { setDialogOpen(false); resetForm(); } });
        }
    };

    const handleDelete = (p: InventarPolozka) => {
        if (window.confirm(`Opravdu chcete vyřadit "${p.nazev}"?`)) {
            deleteMutation.mutate({ id: p.id, duvod: 'Vyřazeno uživatelem' });
        }
    };

    const cekajiciCount = polozky?.filter(p => p.stavInventare === 'CEKA_NA_INVENTARNI_CISLO').length ?? 0;

    const closeDoplnitDialog = () => {
        setDoplnitDialogOpen(false);
        setDoplnitCisla({});
    };

    const vyplnenaCisla = (cekajiciPolozky ?? [])
        .map(p => ({ polozkaId: p.id, inventarniCislo: (doplnitCisla[p.id] ?? '').trim() }))
        .filter(p => p.inventarniCislo.length > 0);

    const handleDoplnitSubmit = () => {
        if (vyplnenaCisla.length === 0) return;
        doplnInventarniCislaMutation.mutate({ polozky: vyplnenaCisla }, {
            onSuccess: () => closeDoplnitDialog(),
        });
    };

    return (
        <Box>
            <PageHeader
                title="Inventář vybavení"
                icon={<InventoryIcon />}
                action={
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <ViewToggle value={viewMode} onChange={setViewMode} />
                        <Tooltip title="Nastavení inventáře">
                            <IconButton onClick={() => navigate('/admin/inventar/nastaveni')}>
                                <SettingsIcon />
                            </IconButton>
                        </Tooltip>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                            Přidat
                        </Button>
                    </Box>
                }
            />

            <Alert severity="info" sx={{ mb: 2 }}>
                Beta verze — modul Inventář slouží k otestování funkcí. Data a rozhraní se mohou změnit.
            </Alert>

            {cekajiciCount > 0 && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={() => setDoplnitDialogOpen(true)}>
                            Doplnit čísla
                        </Button>
                    }
                >
                    {cekajiciCount} {cekajiciCount === 1 ? 'položka čeká' : 'položek čeká'} na inventární číslo od účetní.
                </Alert>
            )}

            {/* Floating toolbar pro hromadné akce */}
            <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <IconButton
                    size="small"
                    onClick={() => {
                        if (polozky && selectedIds.size === polozky.length) {
                            setSelectedIds(new Set());
                        } else {
                            setSelectedIds(new Set(polozky?.map(p => p.id) ?? []));
                        }
                    }}
                >
                    {polozky && selectedIds.size === polozky.length && polozky.length > 0
                        ? <CheckBoxIcon color="primary" fontSize="small" />
                        : <CheckBoxBlankIcon fontSize="small" />}
                </IconButton>
                <Typography variant="body2" sx={{ mr: 1 }}>
                    {selectedIds.size > 0 ? `${selectedIds.size} vybráno` : 'Vybrat vše'}
                </Typography>
                {selectedIds.size > 0 ? (
                    <>
                        <Button size="small" startIcon={<QrIcon />} onClick={() => setQrDialogOpen(true)}>
                            QR štítky
                        </Button>
                        <Button size="small" startIcon={<PrintIcon />} onClick={() => downloadTiskPdfMutation.mutate({ rezim: 'SEZNAM', ids: Array.from(selectedIds) })}>
                            Seznam
                        </Button>
                        <Button size="small" color="inherit" startIcon={<CloseIcon />} onClick={() => setSelectedIds(new Set())}>
                            Zrušit
                        </Button>
                    </>
                ) : (
                    <>
                        <Button size="small" startIcon={<QrIcon />} onClick={() => setQrDialogOpen(true)}>
                            QR {kategorieFilter ? 'kategorie' : 'vše'}
                        </Button>
                        <Button size="small" startIcon={<PrintIcon />} onClick={() => downloadTiskPdfMutation.mutate({ rezim: 'SEZNAM', kategorieId: kategorieFilter as number || undefined })}>
                            Seznam {kategorieFilter ? 'kategorie' : 'vše'}
                        </Button>
                    </>
                )}
            </Paper>

            {/* Filter */}
            <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Kategorie</InputLabel>
                    <Select
                        value={kategorieFilter}
                        onChange={(e) => setKategorieFilter(e.target.value as number | '')}
                        label="Kategorie"
                    >
                        <MenuItem value="">Všechny</MenuItem>
                        {kategorie?.map(k => (
                            <MenuItem key={k.id} value={k.id}>
                                {k.nazev} ({k.pocetPolozek})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button size="small" onClick={() => navigate('/admin/inventar/kategorie')}>
                    Spravovat kategorie
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    {polozky?.length ?? 0} položek
                </Typography>
            </Paper>

            {/* Content */}
            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : !polozky?.length ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <InventoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography color="text.secondary">Zatím žádné položky inventáře</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        1. Vytvořte kategorie (Kategorie) · 2. Přidejte položky a vytiskněte QR štítky · 3. Přidělujte vybavení členům z detailu položky
                    </Typography>
                    <Button sx={{ mt: 2 }} variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>
                        Přidat první položku
                    </Button>
                </Paper>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {polozky.map(p => (
                        <Card key={p.id} variant="outlined" sx={{ bgcolor: selectedIds.has(p.id) ? 'action.selected' : undefined }}>
                            <CardActionArea onClick={() => navigate(`/admin/inventar/${p.id}`)}>
                                <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
                                    <IconButton
                                        size="small"
                                        sx={{ mr: 1 }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setSelectedIds(prev => {
                                                const next = new Set(prev);
                                                if (next.has(p.id)) next.delete(p.id); else next.add(p.id);
                                                return next;
                                            });
                                        }}
                                    >
                                        {selectedIds.has(p.id) ? <CheckBoxIcon color="primary" fontSize="small" /> : <CheckBoxBlankIcon fontSize="small" />}
                                    </IconButton>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="subtitle1" noWrap fontWeight={500}>
                                            {p.nazev}
                                            {p.velikost && <Typography component="span" variant="body2" color="text.secondary"> (vel. {p.velikost})</Typography>}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                            <Chip
                                                icon={React.cloneElement(getKategorieIcon(kategorieIconMap.get(p.kategorieId)) as React.ReactElement<any>, { style: { fontSize: 16 } })}
                                                label={p.kategorieNazev}
                                                size="small"
                                                variant="outlined"
                                            />
                                            {p.serioveCislo && <Chip label={`SN: ${p.serioveCislo}`} size="small" variant="outlined" />}
                                            {p.inventarniCislo && <Chip label={`INV: ${p.inventarniCislo}`} size="small" variant="outlined" />}
                                            <PrirazeniChip typ={p.prirazeniTyp} nazev={p.prirazeniNazev} />
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 2 }}>
                                        <StavChip stav={p.stav} />
                                        <StavInventareChip stav={p.stavInventare} />
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                setMenuPolozka(p);
                                                setMenuAnchorEl(e.currentTarget);
                                            }}
                                        >
                                            <MoreVertIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    ))}
                </Box>
            )}

            {/* Context menu */}
            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={() => setMenuAnchorEl(null)}>
                <MenuItem onClick={() => { if (menuPolozka) { openEdit(menuPolozka); setMenuAnchorEl(null); } }}>
                    <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Upravit</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { if (menuPolozka) { navigate(`/admin/inventar/${menuPolozka.id}`); setMenuAnchorEl(null); } }}>
                    <ListItemIcon><InventoryIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Detail</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { if (menuPolozka) { handleDelete(menuPolozka); setMenuAnchorEl(null); } }}>
                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText sx={{ color: 'error.main' }}>Vyřadit</ListItemText>
                </MenuItem>
            </Menu>

            {/* Create / Edit Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingId ? 'Upravit položku' : 'Nová položka inventáře'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Název"
                            required
                            value={formNazev}
                            onChange={(e) => setFormNazev(e.target.value)}
                            placeholder="Přilba Gallet F1 XF"
                        />
                        <FormControl required>
                            <InputLabel>Kategorie</InputLabel>
                            <Select
                                value={formKategorieId}
                                onChange={(e) => setFormKategorieId(e.target.value as number)}
                                label="Kategorie"
                            >
                                {kategorie?.map(k => (
                                    <MenuItem key={k.id} value={k.id}>{k.nazev}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Výrobce" fullWidth value={formVyrobce} onChange={(e) => setFormVyrobce(e.target.value)} placeholder="Gallet" />
                            <TextField label="Model" fullWidth value={formModel} onChange={(e) => setFormModel(e.target.value)} placeholder="F1 XF" />
                        </Box>
                        <TextField label="Výrobní číslo (SN)" fullWidth value={formSerioveCislo} onChange={(e) => setFormSerioveCislo(e.target.value)} />

                        {/* Dynamický formulář velikosti */}
                        <FormControl size="small">
                            <InputLabel>Typ velikosti</InputLabel>
                            <Select
                                value={formVelikostTypId}
                                onChange={(e) => {
                                    setFormVelikostTypId(e.target.value as number | '');
                                    setFormVelikostValues([]);
                                    setFormVelikost('');
                                }}
                                label="Typ velikosti"
                            >
                                <MenuItem value="">Bez velikosti</MenuItem>
                                {velikostTypy?.map((t: any) => (
                                    <MenuItem key={t.id} value={t.id}>{t.nazev}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {velikostPole.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                {velikostPole.map((pole, i) => (
                                    <FormControl key={i} size="small" sx={{ flex: 1 }}>
                                        <InputLabel>{pole.label}</InputLabel>
                                        <Select
                                            value={formVelikostValues[i] ?? ''}
                                            onChange={(e) => {
                                                const vals = [...formVelikostValues];
                                                vals[i] = e.target.value as string;
                                                setFormVelikostValues(vals);
                                            }}
                                            label={pole.label}
                                        >
                                            {pole.hodnoty.map(h => (
                                                <MenuItem key={h} value={h}>{h}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ))}
                            </Box>
                        )}
                        <Button size="small" onClick={() => navigate('/admin/inventar/velikost-typy')}>
                            Spravovat typy velikostí
                        </Button>
                        <FormControl size="small">
                            <InputLabel>Typ revize</InputLabel>
                            <Select
                                value={formTypKontrolyId}
                                onChange={(e) => setFormTypKontrolyId(e.target.value as number | '')}
                                label="Typ revize"
                            >
                                <MenuItem value="">Bez revizí</MenuItem>
                                {typyRevizeOOP.map(t => (
                                    <MenuItem key={t.id} value={t.id}>{t.nazev}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Inventární číslo (od účetní)"
                            value={formInventarniCislo}
                            onChange={(e) => setFormInventarniCislo(e.target.value)}
                            helperText="Nepovinné — pokud necháte prázdné, položka bude čekat na přidělení čísla od účetní"
                        />
                        <TextField
                            label="Poznámka"
                            multiline
                            rows={2}
                            value={formPoznamka}
                            onChange={(e) => setFormPoznamka(e.target.value)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setDialogOpen(false); resetForm(); }}>Zrušit</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!formNazev || !formKategorieId || createMutation.isPending || updateMutation.isPending}
                    >
                        {editingId ? 'Uložit' : 'Vytvořit'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog výběr formátu QR štítků */}
            <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Tisk QR štítků</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel>Formát štítků</InputLabel>
                            <Select
                                value={qrDialogFormat}
                                onChange={(e) => setQrDialogFormat(e.target.value)}
                                label="Formát štítků"
                            >
                                {QR_FORMATY.map(f => (
                                    <MenuItem key={f.kod} value={f.kod}>{f.nazev}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Typography variant="body2" color="text.secondary">
                            {selectedIds.size > 0
                                ? `${selectedIds.size} vybraných položek`
                                : kategorieFilter
                                    ? `Všechny položky ve zvolené kategorii`
                                    : `Všechny aktivní položky (${polozky?.length ?? 0})`}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setQrDialogOpen(false)}>Zrušit</Button>
                    <Button variant="contained" startIcon={<QrIcon />} onClick={() => {
                        const ids = selectedIds.size > 0 ? Array.from(selectedIds) : undefined;
                        const katId = !ids && kategorieFilter ? kategorieFilter as number : undefined;
                        downloadTiskPdfMutation.mutate({ rezim: 'QR', ids, kategorieId: katId, format: qrDialogFormat });
                        setQrDialogOpen(false);
                    }}>
                        Stáhnout PDF
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog hromadné doplnění inventárních čísel */}
            <Dialog open={doplnitDialogOpen} onClose={closeDoplnitDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Doplnit inventární čísla</DialogTitle>
                <DialogContent>
                    {cekajiciLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : cekajiciPolozky && cekajiciPolozky.length > 0 ? (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Vyplňte inventární číslo u položek, které chcete doplnit. Prázdné řádky se přeskočí a doplní se později.
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Položka</TableCell>
                                            <TableCell>Kategorie</TableCell>
                                            <TableCell>Inventární číslo</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {cekajiciPolozky.map(p => (
                                            <TableRow key={p.id}>
                                                <TableCell>
                                                    {p.nazev}
                                                    {p.velikost && ` (${p.velikost})`}
                                                </TableCell>
                                                <TableCell>{p.kategorieNazev}</TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        placeholder="Inventární číslo"
                                                        value={doplnitCisla[p.id] ?? ''}
                                                        onChange={(e) => setDoplnitCisla(prev => ({ ...prev, [p.id]: e.target.value }))}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    ) : (
                        <Typography color="text.secondary">Žádné položky nečekají na inventární číslo.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDoplnitDialog}>Zrušit</Button>
                    <Button
                        variant="contained"
                        onClick={handleDoplnitSubmit}
                        disabled={vyplnenaCisla.length === 0 || doplnInventarniCislaMutation.isPending}
                    >
                        Doplnit {vyplnenaCisla.length > 0 ? `(${vyplnenaCisla.length})` : ''}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default InventarPage;
